import { createRoute, Link } from "@tanstack/solid-router";
import { queryKeys, restoreBookmarkMutation, softDeleteBookmarkMutation } from "@bso/shared";
import { appRoute } from "./route";
import { createMemo, For, Show } from "solid-js";
import { Bookmark, BookOpen, Calendar, Trash2 } from "@fleet/icons";
import { getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { EmptyIllustration } from "@/components/ui/empty-illustration";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { compareVerseRefs } from "@/utils/verse-collections";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { registerUndo } from "@/lib/undo";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { and, bible_bookmark, eq, isNull } from "@bso/powersync";

interface BookmarkItem {
  id: string;
  version: string;
  book: number;
  chapter: number;
  verse: number;
  createdAt: string;
}

export const bookmarksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bookmarks",
  component: BookmarksPage,
});

function BookmarksPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  // PowerSync reactive query for the user's bookmarks
  const bookmarksQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.bookmarks.mine(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_bookmark.id,
          version: bible_bookmark.version,
          book: bible_bookmark.book,
          chapter: bible_bookmark.chapter,
          verse: bible_bookmark.verse,
          createdAt: bible_bookmark.created_at,
        })
        .from(bible_bookmark)
        .where(and(eq(bible_bookmark.user_id, u), isNull(bible_bookmark.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  const bookmarks = createMemo(() => {
    const list: BookmarkItem[] = [];
    for (const r of bookmarksQuery.data ?? []) {
      if (r.book === null || r.chapter === null || r.verse === null) {
        continue;
      }
      list.push({
        id: r.id,
        version: r.version ?? "kjv",
        book: r.book,
        chapter: r.chapter,
        verse: r.verse,
        createdAt: r.createdAt ?? new Date().toISOString(),
      });
    }
    // Canonical order (book → chapter → verse)
    list.sort(compareVerseRefs);
    return list;
  });

  // Logical delete (tombstone for sync). Soft-delete -> undo restores it (L1) and
  // also recoverable from trash (L2, /profile/trash).
  const handleDelete = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await softDeleteBookmarkMutation(dbInstance, id);

      registerUndo(
        {
          done: t(msg`Bookmark moved to trash`),
          undoLabel: t(msg`Undo`),
          undone: t(msg`Undone`),
          failed: t(msg`Couldn't undo`),
        },
        async () => {
          await restoreBookmarkMutation(dbInstance, id);
        },
      );
    } catch (e) {
      console.error("Failed to delete bookmark:", e);
    }
  };

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Bookmark class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Bookmarks`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">{t(msg`Your favorite verses`)}</p>
          </div>
        </div>
      </div>

      {/* List */}
      <div class="flex-1 overflow-y-auto p-4 md:p-8">
        <Show
          when={bookmarks().length > 0}
          fallback={
            <div class="max-w-md mx-auto py-20 text-center border border-dashed border-border/30 bg-card/10 rounded-2xl p-6 motion-safe:animate-fade-in">
              <EmptyIllustration variant="bookmarks" class="mb-3 inline-flex" />
              <h3 class="text-sm font-bold text-foreground/85">{t(msg`No bookmarks yet`)}</h3>
              <p class="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                {t(msg`Bookmark a verse while reading to find it again quickly.`)}
              </p>
            </div>
          }
        >
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-24"
            data-testid="bookmarks-list"
          >
            <For each={bookmarks()}>
              {(bm) => (
                <div class="group relative flex flex-col bg-card/30 border border-border/35 hover:border-primary/30 rounded-2xl overflow-hidden shadow-2xs hover:shadow-xs transition-all">
                  <div class="h-1.5 w-full shrink-0 bg-amber-500" />
                  <div class="p-5 flex flex-col justify-between flex-1 space-y-4">
                    <div class="space-y-2.5">
                      <span class="text-3xs text-muted-foreground font-mono flex items-center gap-1.5">
                        <Calendar class="w-3 h-3" />
                        {new Date(bm.createdAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <h3 class="text-sm font-extrabold text-foreground/90 font-serif leading-snug flex items-center gap-1.5">
                        <Bookmark class="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                        <span>
                          {t(getBookNameDescriptor(bm.book))} {bm.chapter}:{bm.verse}
                        </span>
                        <span class="text-3xs uppercase tracking-wider font-extrabold text-muted-foreground">
                          {bm.version}
                        </span>
                      </h3>
                    </div>
                    <div class="flex items-center justify-between border-t border-border/20 pt-3">
                      <Link
                        to="/bible/$version/$bookAbbr/$chapter"
                        params={{
                          version: bm.version,
                          bookAbbr: idToAbbr(bm.book),
                          chapter: String(bm.chapter),
                        }}
                        search={{ verse: bm.verse }}
                        class="text-xs font-bold text-primary hover:text-primary/80 flex items-center gap-1.5 transition-colors"
                      >
                        <BookOpen class="w-3.5 h-3.5" />
                        <span>{t(msg`Read passage`)}</span>
                      </Link>
                      <button
                        type="button"
                        disabled={!drizzle()}
                        onClick={() => {
                          void handleDelete(bm.id);
                        }}
                        class="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t(msg`Remove bookmark`)}
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}
